import { useState } from "react";
import {
  Plus, Check, Trash2, CheckCircle2
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const TASK_CATEGORIES = [
  { value: "haccp", label: "HACCP Kontrolle", color: "text-red-500" },
  { value: "temperature", label: "Temperatur", color: "text-blue-500" },
  { value: "cleaning", label: "Reinigung", color: "text-cyan-500" },
  { value: "pest", label: "Schaedlingskontrolle", color: "text-orange-500" },
  { value: "storage", label: "Lagerung", color: "text-purple-500" },
  { value: "opening", label: "Oeffnung", color: "text-green-500" },
  { value: "closing", label: "Schliessung", color: "text-yellow-500" },
];

const AufgabenTab = ({ tasks, getCategoryInfo, onDelete, onComplete, onAdd }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [newTask, setNewTask] = useState({
    title: "", description: "", category: "haccp",
    assigned_role: "all", priority: "medium", frequency: "daily"
  });

  const handleSubmit = () => {
    if (!newTask.title.trim()) return;
    onAdd(newTask);
    setShowDialog(false);
    setNewTask({
      title: "", description: "", category: "haccp",
      assigned_role: "all", priority: "medium", frequency: "daily"
    });
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-white">Aufgaben ({tasks.length})</h2>
        <Button onClick={() => setShowDialog(true)} className="bg-violet-600 hover:bg-violet-700 gap-2" data-testid="add-task-btn">
          <Plus size={16} /> Neue Aufgabe
        </Button>
      </div>

      {tasks.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-slate-700">
          <CheckCircle2 size={48} className="mx-auto text-slate-600 mb-4" />
          <p className="text-slate-400">Keine Aufgaben vorhanden</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => {
            const catInfo = getCategoryInfo(task.category);
            const CatIcon = catInfo.icon;
            const isCompleted = task.status === "completed";
            return (
              <div key={task.id} className={`bg-slate-800 border border-slate-700 rounded-xl p-4 ${isCompleted ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <button onClick={() => !isCompleted && onComplete(task.id)}
                      className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        isCompleted ? 'bg-green-500 border-green-500' : 'border-slate-500 hover:border-green-500'}`}>
                      {isCompleted && <Check size={12} className="text-white" />}
                    </button>
                    <div>
                      <h3 className={`font-semibold ${isCompleted ? 'line-through text-slate-400' : 'text-white'}`}>{task.title}</h3>
                      {task.description && <p className="text-sm text-slate-400 mt-1">{task.description}</p>}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${catInfo.color} bg-slate-700`}>
                          <CatIcon size={12} /> {catInfo.label}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          task.priority === 'high' ? 'bg-red-900/30 text-red-400'
                            : task.priority === 'medium' ? 'bg-yellow-900/30 text-yellow-400'
                              : 'bg-slate-700 text-slate-400'}`}>
                          {task.priority === 'high' ? 'Hoch' : task.priority === 'medium' ? 'Mittel' : 'Niedrig'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => onDelete(task.id)}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700">
          <DialogHeader><DialogTitle className="text-white">Neue Aufgabe erstellen</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Titel</Label>
              <Input placeholder="z.B. Kuehlschrank Temperatur pruefen" value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} className="bg-slate-700 border-slate-600" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Beschreibung</Label>
              <Textarea placeholder="Optional" value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} className="bg-slate-700 border-slate-600" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Kategorie</Label>
                <Select value={newTask.category} onValueChange={(v) => setNewTask({ ...newTask, category: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent>{TASK_CATEGORIES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Prioritaet</Label>
                <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">Hoch</SelectItem>
                    <SelectItem value="medium">Mittel</SelectItem>
                    <SelectItem value="low">Niedrig</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleSubmit} className="w-full bg-violet-600 hover:bg-violet-700">Aufgabe erstellen</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AufgabenTab;
